/**
 * GroupDetailsDialog Component
 * Shows detailed information about a group with tabbed interface
 *
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import { Button, Tabs, Tab, DialogContent, Box, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from '../ui/BaseDialog';
import { DeleteConfirmationDialog } from '../ui/DeleteConfirmationDialog';
import { useDeleteGroup } from '../../hooks/api/useGroups';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useGroupDetails } from '@/hooks/business/useGroupDetails';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { GroupDetailsHeader } from './GroupDetailsHeader';
import { GroupOverviewTab } from './GroupOverviewTab';
import { GroupMembersTab } from './GroupMembersTab';
import { EditGroupDialog } from './EditGroupDialog';
import type { AppGroup } from '../../types/group';

interface GroupDetailsDialogProps {
  group: AppGroup;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const GroupDetailsDialog: React.FC<GroupDetailsDialogProps> = ({
  group,
  open,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const {
    activeTab,
    setActiveTab,
    syncing,
    error,
    handleSyncMembers,
  } = useGroupDetails(open);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { can } = usePermissions();
  const { showSuccess, showError } = useSnackbar();
  const deleteGroupMutation = useDeleteGroup();

  const isExternalGroup = (group.source ?? 'local') !== 'local';

  // Check if current user can manage group members
  const canManageMembers = (() => {
    // Admin and moderators can manage all groups
    if (can.canCreateGroups) {
      return true;
    }

    // Only local groups can be managed (external groups are read-only)
    return !isExternalGroup;
  })();

  const onSyncMembers = () => handleSyncMembers(group, onUpdate);

  const handleEditGroup = () => {
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    onUpdate();
  };

  // Deleting archives the group and withdraws the access its members had
  // through it, so it always goes through a confirmation step.
  const handleDeleteGroup = async () => {
    try {
      await deleteGroupMutation.mutateAsync(group.id);
      showSuccess(t('groups.actions.deleteSuccess'));
      setDeleteDialogOpen(false);
      onUpdate();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('groups.actions.deleteError'));
      setDeleteDialogOpen(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <GroupOverviewTab group={group} />;
      case 1:
        return (
          <GroupMembersTab
            group={group}
            syncing={syncing}
            onSyncMembers={onSyncMembers}
            canManageMembers={canManageMembers}
          />
        );
      default:
        return null;
    }
  };

  const memberCount = (group.members || []).length;

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title="" // Using custom header
        maxWidth="lg"
        fullWidth
        error={error}
        actions={
          <>
            {!isExternalGroup && (
              <Button startIcon={<EditIcon />} onClick={handleEditGroup}>
                {t('groups.actions.editGroup')}
              </Button>
            )}
            {!isExternalGroup && can.canCreateGroups && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t('groups.actions.deleteGroup')}
              </Button>
            )}
            <Button onClick={onClose}>{t('groups.details.close')}</Button>
          </>
        }
      >
        <GroupDetailsHeader group={group} onClose={onClose} />

        <DialogContent sx={{ pt: 0 }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
            <Tab label={t('groups.details.tabs.overview')} />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t('groups.details.tabs.members')}
                  {memberCount > 0 && (
                    <Chip
                      size="small"
                      label={memberCount}
                      sx={{ height: 20, minWidth: 20, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>

          {renderTabContent()}
        </DialogContent>
      </BaseDialog>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => void handleDeleteGroup()}
        title={t('groups.actions.deleteGroup')}
        itemName={group.name}
        message={t('groups.actions.deleteConfirm', { name: group.name })}
        confirmLabel={t('groups.actions.deleteGroup')}
        loading={deleteGroupMutation.isPending}
        showDeleteIcon
      />

      <EditGroupDialog
        group={group}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};
